import { Box, Chip, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useTenderDetails } from '../../contexts/TenderDetailsContext';
import type { User } from '../../types/auth';
import PartnerDelegationCard from './PartnerDelegationCard';
import TenderInfoCard from './TenderInfoCard';
import TenderScopeCard from './TenderScopeCard';
import TenderTeamCard from './TenderTeamCard';
import UpdateTenderContextCard from './UpdateTenderContextCard';
import WorkflowStep from './WorkflowStep';

const TenderContextStep: React.FC = () => {
  const _theme = useTheme();
  const {
    tender,
    hasAddedContextFiles,
    isPartnerView,
    account,
    contributors,
    users,
    handleUpdateTender,
    handleRegenerate,
    toggleStepCompletion,
    openStep,
    setOpenStep,
  } = useTenderDetails();

  return (
    <WorkflowStep
      stepNumber={1}
      title={tender.isGrant ? 'Grant Context' : 'Tender Context'}
      expanded={openStep === 1}
      onChange={(_, isExpanded) => setOpenStep(isExpanded ? 1 : null)}
      description={
        <Box>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', display: 'inline-block', fontSize: '1rem' }}
          >
            Review the {tender.isGrant ? 'grant' : 'tender'} details and scope.
          </Typography>
          {tender.noFiles && !hasAddedContextFiles && (
            <Chip
              label="No Docs, Visit Portal"
              component="a"
              href={tender.tenderSourceURL || ''}
              target="_blank"
              clickable
              size="small"
              variant="outlined"
              sx={{
                ml: 1,
                fontWeight: 'bold',
                backgroundColor: _theme.palette.greyContrast,
                color: _theme.palette.primary.contrastText,
                borderColor: _theme.palette.bidlogiq.orange,
                borderWidth: 2,
                fontSize: '0.85rem',
                '&&:hover': {
                  backgroundColor: _theme.palette.bidlogiq.brick,
                  color: '#fff',
                  borderColor: _theme.palette.bidlogiq.brick,
                },
              }}
            />
          )}
        </Box>
      }
      isComplete={true}
      onToggleComplete={() => toggleStepCompletion(1)}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TenderInfoCard tender={tender} onUpdate={handleUpdateTender} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TenderScopeCard tender={tender} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          {tender.keywords && (
            <>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', mb: 0.5 }}>
                <strong>Keywords:</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {(Array.isArray(tender.keywords)
                  ? tender.keywords
                  : tender.keywords.split(',').map((kw: string) => kw.trim())
                )
                  .map((kw: string) => String(kw).replace(/_/g, ' ').replace(/\s+/g, ' ').trim())
                  .filter((kw: string) => kw)
                  .map((keyword: string) => (
                    <Chip
                      key={keyword}
                      label={keyword}
                      size="small"
                      sx={{
                        backgroundColor: (theme) => theme.palette.bidlogiq.rust,
                        color: 'white',
                        fontWeight: '500',
                        fontSize: '0.75rem',
                      }}
                    />
                  ))}
              </Box>
            </>
          )}
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <UpdateTenderContextCard
                tender={tender}
                onUpdateContext={async () => {
                  if (tender.noFiles && hasAddedContextFiles) {
                    try {
                      await handleUpdateTender('noFiles', false);
                    } catch (_e) {
                      // Ignore error
                    }
                  }
                  handleRegenerate('alignmentAnalysis');
                }}
                isPartnerView={!!isPartnerView}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {!isPartnerView ? (
                <TenderTeamCard
                  tender={tender}
                  contributors={contributors}
                  users={users}
                  currentUser={account as User | null}
                  onUpdate={handleUpdateTender}
                />
              ) : (
                <PartnerDelegationCard
                  tender={tender}
                  users={users}
                  currentUser={account as User | null}
                  onUpdate={handleUpdateTender}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </WorkflowStep>
  );
};

export default TenderContextStep;
